import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '../../../apiClients/workspace/types';
import { Avatar } from '../../../components/shared';
import { formatAmount } from '../../../lib/utils';
import { AccountType, UserAccountData } from '../api/types';

interface UserBalancesListProps {
  users: Map<string, User>;
  userAccounts: Map<string, UserAccountData>;
  isLoading: boolean;
  sortBy: 'name' | 'balance';
  sortOrder: 'asc' | 'desc';
  searchQuery: string;
  onSortChange: (sortBy: 'name' | 'balance', sortOrder: 'asc' | 'desc') => void;
}

const UserBalancesList: React.FC<UserBalancesListProps> = observer(
  ({
    users,
    userAccounts,
    isLoading,
    sortBy,
    sortOrder,
    searchQuery,
    onSortChange,
  }) => {
    const { t } = useTranslation();

    const data = useMemo(
      () =>
        Array.from(users.values())

          .map((user) => {
            const accountData = userAccounts.get(user.id);
            const mainAccount = accountData?.accounts.find(
              (account) => account.typeId === AccountType.Main
            );

            const balance = mainAccount ? Number(mainAccount.balance) : 0;
            const formattedBalance = mainAccount
              ? formatAmount(mainAccount.balance)
              : '0.00';

            return {
              user,
              balance,
              formattedBalance,
            };
          }),
      [users, userAccounts]
    );

    const sortedData = useMemo(
      () =>
        data
          .filter(({ user }) => {
            if (!searchQuery.trim()) return true;
            return user.name.toLowerCase().includes(searchQuery.toLowerCase());
          })
          .sort((a, b) => {
            let comparison = 0;

            if (sortBy === 'name') {
              comparison = a.user.name.localeCompare(b.user.name);
            } else if (sortBy === 'balance') {
              comparison = a.balance - b.balance;
            }

            return sortOrder === 'asc' ? comparison : -comparison;
          }),
      [data, sortBy, sortOrder, searchQuery]
    );

    const handleSort = (newSortBy: 'name' | 'balance') => {
      if (sortBy === newSortBy) {
        // Toggle sort order if same column
        onSortChange(newSortBy, sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        // Set new column with ascending order
        onSortChange(newSortBy, 'asc');
      }
    };

    const getSortIcon = (column: 'name' | 'balance') => {
      if (sortBy !== column) {
        return <span className="text-gray-400">↕</span>;
      }
      return sortOrder === 'asc' ? <span>↑</span> : <span>↓</span>;
    };

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">{t('common.loading')}</span>
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-4">👥</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery.trim()
              ? t('billing.balances.noSearchResults')
              : t('billing.balances.noUsers')}
          </h3>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto overscroll-contain">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>{t('billing.balances.userName')}</span>
                  {getSortIcon('name')}
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('balance')}
              >
                <div className="flex items-center space-x-1">
                  <span>{t('billing.balances.balance')}</span>
                  {getSortIcon('balance')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.map(({ user, formattedBalance, balance }) => (
              <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Avatar
                        src={user.photoUrl}
                        alt={user.name}
                        size="sm"
                        className="flex-shrink-0"
                      />
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {user.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div
                    className={`text-sm font-medium ${
                      balance >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {formattedBalance}&nbsp;₽
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
);

export default UserBalancesList;
